import type { ChangeEvent } from 'react';
import { useEffect, useRef, useState } from 'react';

import type { ResultsType } from 'src/components/molecules/AddressSearch/components/Results';
import { useLoqateSearch } from 'src/components/molecules/AddressSearch/hooks/useLoqateSearch';
import type { Address } from 'src/components/molecules/AddressSearch/services/getAddress';
import { getAddress } from 'src/components/molecules/AddressSearch/services/getAddress';
import type { AddressSearchResult } from 'src/components/molecules/AddressSearch/services/loqate';
import { SEARCH_RESULT_TYPE_ADDRESS } from 'src/components/molecules/AddressSearch/services/loqate';

export interface UseAddressSearchData {
  containerId: string;
  handleBlur: () => void;
  handleChange: (e: ChangeEvent<HTMLInputElement>) => void;
  handleClearContainer: () => void;
  handleFocus: () => void;
  handleSelection: (item: AddressSearchResult | null) => void;
  inputRef: React.RefObject<HTMLInputElement>;
  query: string;
  results: AddressSearchResult[] | null;
  resultsPaneRef: React.RefObject<ResultsType>;
  showResults: boolean;
}

export const useAddressSearch = (
  country: string,
  onAddressSelected: (address: Address) => void,
  onQueryChanged: (event: ChangeEvent<HTMLInputElement>) => void,
  onNoResults: (query: string) => void,
  onLookupError: (query: string) => void,
): UseAddressSearchData => {
  const [isFocused, setIsFocused] = useState(false);
  const [retrievalError, setRetrievalError] = useState(false);
  const {
    containerId,
    error: searchError,
    query,
    results,
    setContainerId,
    setQuery,
  } = useLoqateSearch(country, 200);
  const resultsPaneRef = useRef<ResultsType>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const canShowResults = isFocused && !retrievalError && !searchError;
  const hasNoResults = results && !results.length && query.length;
  const showResults = !!results && canShowResults;

  useEffect(() => {
    if (canShowResults && hasNoResults) {
      onNoResults(query);
    }
  }, [canShowResults, hasNoResults, onNoResults, query]);

  useEffect(() => {
    if (retrievalError || searchError) {
      onLookupError(query);
    }
  }, [retrievalError, searchError, onLookupError, query]);

  useEffect(() => {
    if (resultsPaneRef.current) {
      resultsPaneRef.current.scrollTop = 0;
    }
  }, [containerId]);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    onQueryChanged(e);
    setRetrievalError(false);
    setContainerId('');
    setQuery(e.target.value);
  };

  const handleBlur = () => setIsFocused(false);

  const handleFocus = () => setIsFocused(true);

  const handleSelection = (item: AddressSearchResult | null) => {
    if (!item) {
      setContainerId('');
      setQuery('');
      inputRef.current?.blur();
      return;
    }

    const { Id, Type } = item;
    if (Type === SEARCH_RESULT_TYPE_ADDRESS) {
      handleSelectedAddress(Id);
    } else {
      setContainerId(Id);
    }
  };

  const handleSelectedAddress = async (id: string) => {
    try {
      const address = await getAddress(id, country);

      if (address) {
        setRetrievalError(false);
        setContainerId('');
        setQuery('');
        onAddressSelected(address);
      } else {
        setRetrievalError(true);
      }
    } catch (e) {
      setRetrievalError(true);
    }
  };

  const handleClearContainer = () => setContainerId('');

  return {
    query,
    results,
    showResults,
    containerId,
    inputRef,
    resultsPaneRef,
    handleChange,
    handleBlur,
    handleFocus,
    handleSelection,
    handleClearContainer,
  };
};
